import { supabase } from '@/integrations/supabase/client';

export interface NavigationItem {
  id: string;
  label: string;
  url: string;
  location: 'header' | 'footer_col1' | 'footer_col2' | 'footer_col3' | 'footer_legal';
  parentId: string | null;
  sortOrder: number;
  isExternal: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function mapNavItem(item: any): NavigationItem {
  return {
    id: item.id,
    label: item.label,
    url: item.url,
    location: item.location,
    parentId: item.parent_id,
    sortOrder: item.sort_order,
    isExternal: item.is_external,
    isActive: item.is_active,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

export async function getNavigationItems(location?: string): Promise<NavigationItem[]> {
  let query = supabase
    .from('navigation_items')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (location) {
    query = query.eq('location', location);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapNavItem);
}

export async function getAllNavigationItems(location?: string): Promise<NavigationItem[]> {
  let query = supabase
    .from('navigation_items')
    .select('*')
    .order('sort_order', { ascending: true });

  if (location) {
    query = query.eq('location', location);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapNavItem);
}

export async function createNavigationItem(data: {
  label: string;
  url: string;
  location: string;
  isExternal?: boolean;
  sortOrder?: number;
}): Promise<NavigationItem> {
  const { data: result, error } = await supabase
    .from('navigation_items')
    .insert({
      label: data.label,
      url: data.url,
      location: data.location,
      is_external: data.isExternal || false,
      sort_order: data.sortOrder || 0,
    })
    .select()
    .single();

  if (error) throw error;
  return mapNavItem(result);
}

export async function updateNavigationItem(
  id: string,
  updates: Partial<{
    label: string;
    url: string;
    location: string;
    isExternal: boolean;
    isActive: boolean;
    sortOrder: number;
  }>
): Promise<NavigationItem> {
  const dbUpdates: any = { updated_at: new Date().toISOString() };
  if (updates.label !== undefined) dbUpdates.label = updates.label;
  if (updates.url !== undefined) dbUpdates.url = updates.url;
  if (updates.location !== undefined) dbUpdates.location = updates.location;
  if (updates.isExternal !== undefined) dbUpdates.is_external = updates.isExternal;
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
  if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;

  const { data, error } = await supabase
    .from('navigation_items')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return mapNavItem(data);
}

export async function deleteNavigationItem(id: string): Promise<void> {
  const { error } = await supabase.from('navigation_items').delete().eq('id', id);
  if (error) throw error;
}

export async function reorderNavigationItems(orderedIds: string[]): Promise<void> {
  for (let i = 0; i < orderedIds.length; i++) {
    await supabase
      .from('navigation_items')
      .update({ sort_order: i + 1, updated_at: new Date().toISOString() })
      .eq('id', orderedIds[i]);
  }
}
